<script setup lang="ts">
import {computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch} from 'vue'
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import {determineUspBoxWidth, getGuid} from '@/ts/utilities.ts'
const RenderAction = defineAsyncComponent(() => import('@/components/helpers/RenderAction.vue'))

interface ProductListUspBullet {
	header?: string,
	teaser?: string,
	icon?: string
}

interface ProductListUspData {
	header?: string,
	teaser?: string,
	type?: string,
	image?: string,
	icon?: string,
	buttonText?: string,
	buttonLink?: string,
	shopvertizePlacementId?: string,
	widthContentBox?: string,
	widthProductListBox?: string,
	widthProductPageBox?: string,
	bulletUsps?: ProductListUspBullet[]
}

interface ShopvertizeApi {
	display: (placementId: string, format: string, containerId: string) => void,
	setCollapseIfNoContent?: (shouldCollapse: boolean) => void,
	startServices?: () => void
}

declare global {
	interface Window {
		shopvertag?: Array<(shopvertize: ShopvertizeApi) => void>
	}
}

interface Props {
	usp?: ProductListUspData,
	context: string,
	layout: string,
	index?: number,
	productCount?: number
}

const props = defineProps<Props>()

const shopvertizeState = ref<'local' | 'pending' | 'ready'>('local')
const shopvertizeContainerId = `shopvertize-product-list-usp-${getGuid()}`
const hasShopvertizePlacement = computed(() => Boolean(props.usp?.shopvertizePlacementId?.trim()))

let shopvertizeFallbackTimeout: ReturnType<typeof setTimeout> | null = null
let shopvertizeMutationObserver: MutationObserver | null = null


const setPositionInGrid = () => {
	if (props.context === 'list') {
		return determineUspBoxWidth(props.usp?.widthProductListBox, 'col-span-1', props.index)
	}

	if (props.context === 'content') {
		return determineUspBoxWidth(props.usp?.widthContentBox)
	}

	if (props.context === 'product') {
		return determineUspBoxWidth(props.usp?.widthProductPageBox)
	}

	return 'col-span-1'
}

const clearShopvertizeHandlers = () => {
	if (shopvertizeFallbackTimeout) {
		clearTimeout(shopvertizeFallbackTimeout)
		shopvertizeFallbackTimeout = null
	}

	if (shopvertizeMutationObserver) {
		shopvertizeMutationObserver.disconnect()
		shopvertizeMutationObserver = null
	}
}

const isShopvertizeNotFoundError = (error: unknown) => {
	const errorMessage = error instanceof Error ? error.message : String(error ?? '')
	return /404|not found|no placement/i.test(errorMessage)
}

const isShopvertizePlacementRendered = () => {
	const container = document.getElementById(shopvertizeContainerId)
	if (!container) return false

	const hasText = Boolean(container.textContent?.trim())
	const hasChildElements = container.childElementCount > 0
	const hasVisibleHeight = container.getBoundingClientRect().height > 0

	return (hasChildElements || hasText) && hasVisibleHeight
}

const showLocalFallback = () => {
	clearShopvertizeHandlers()
	shopvertizeState.value = 'local'
}

const showShopvertizePlacement = () => {
	clearShopvertizeHandlers()
	shopvertizeState.value = 'ready'
}

const observeShopvertizePlacement = () => {
	const container = document.getElementById(shopvertizeContainerId)
	if (!container) {
		return
	}

	if (isShopvertizePlacementRendered()) {
		showShopvertizePlacement()
		return
	}

	shopvertizeMutationObserver = new MutationObserver(() => {
		if (isShopvertizePlacementRendered()) {
			showShopvertizePlacement()
		}
	})

	shopvertizeMutationObserver.observe(container, {
		childList: true,
		subtree: true,
		attributes: true,
		characterData: true,
	})
}

const renderShopvertizePlacement = () => {
	clearShopvertizeHandlers()

	if (!hasShopvertizePlacement.value) {
		shopvertizeState.value = 'local'
		return
	}

	const placementId = props.usp?.shopvertizePlacementId?.trim()
	if (!placementId) {
		shopvertizeState.value = 'local'
		return
	}

	shopvertizeState.value = 'pending'
	window.shopvertag = window.shopvertag || []
	observeShopvertizePlacement()

	window.shopvertag.push((shopvertize: ShopvertizeApi) => {
		try {
			const handle = shopvertize.display(placementId, '0x0', shopvertizeContainerId) as Partial<ShopvertizeApi> | undefined
			if (typeof handle?.setCollapseIfNoContent === 'function') {
				handle.setCollapseIfNoContent(true)
			}
			shopvertize.startServices?.()
			observeShopvertizePlacement()
		} catch (error) {
			if (isShopvertizeNotFoundError(error)) {
				showLocalFallback()
				return
			}

			console.error('Failed to render Shopvertize USP placement', error)
		}
	})
}

onMounted(() => {
	renderShopvertizePlacement()
})

watch(() => props.usp?.shopvertizePlacementId, () => {
	renderShopvertizePlacement()
})

onBeforeUnmount(() => {
	clearShopvertizeHandlers()
})

</script>

<template>
	<div v-if="props.usp" class="rounded border border-grey-light overflow-hidden" :class="setPositionInGrid()">
		<template v-if="shopvertizeState === 'local'">
			<div v-if="props.usp?.image"
					 class="bg-white-warm">
				<img
					:src="'/Admin/Public/GetImage.ashx?Image=' + props.usp?.image + '&Width=600&height=375&Format=webP&Quality=90&Crop=5&resolution=50'"
					:alt="props.usp?.header"
					width="600"
					height="375"
					class="object-cover object-center h-auto aspect-[16/10]"
					loading="lazy"
				>
			</div>
			<div class="flex flex-col gap-y-6 p-8 h-full"
					 :class="props.context == 'list' ? 'bg-white' : 'bg-beige-light'">
				<div v-if="props.usp?.header"
						 class="flex items-center gap-2"
				>
					<font-awesome-icon
						v-if="props.usp?.icon"
						:icon="['fal', props.usp?.icon]"
						class="text-2xl"
					/>
					<h3 class="text-pretty">{{ props.usp?.header }}</h3>
				</div>
				<template v-if="props.usp?.type === 'BULLETS' && props.usp?.bulletUsps">
					<ul class="list-none flex flex-col gap-y-4">
						<li class="flex gap-x-2 items-baseline"
							v-for="(bullet, index) in props.usp?.bulletUsps" :key="index">
							<font-awesome-icon
								v-if="bullet.icon"
								:icon="['fal', bullet.icon]"
							/>
							<p class="flex flex-col">
								<strong>{{ bullet.header }}</strong>
								{{ bullet.teaser }}
							</p>
						</li>
					</ul>
				</template>
				<p v-else>{{ props.usp?.teaser }}</p>
				<RenderAction
					v-if="props.usp?.buttonText && props.usp?.buttonLink"
					action="link"
					theme="link"
					target="external"
					size="none"
					:data="props.usp?.buttonLink"
				>
					{{ props.usp?.buttonText }}
				</RenderAction>
			</div>
		</template>
		<div v-else class="bg-white h-full aspect-[1/1]">
			<div :id="shopvertizeContainerId" class="w-full h-full" :class="{'min-h-[240px]': shopvertizeState === 'pending'}"></div>
		</div>
	</div>
</template>
